import { useCallback, useEffect, useRef, useState } from 'react';

export interface AudioTrack {
    id: string;
    file: File;
    name: string;
    offsetMs: number;
    durationMs: number;
}

export interface MixerState {
    videoFile: File | null;
    videoDurationMs: number;
    videoPreviewUrl: string | null;
    audioTracks: AudioTrack[];
    includeVideoAudio: boolean;
    isProcessing: boolean;
    previewUrl: string | null;
}

interface UseMediaBunnyMixerReturn {
    state: MixerState;
    setVideoFile: (file: File | null) => void;
    addAudioTrack: (file: File) => Promise<void>;
    removeAudioTrack: (id: string) => void;
    updateAudioTrackOffset: (id: string, offsetMs: number) => void;
    setIncludeVideoAudio: (include: boolean) => void;
    generatePreview: () => Promise<void>;
    downloadVideo: () => Promise<void>;
    reset: () => void;
}

const generateId = () => Math.random().toString(36).substring(7);

/**
 * Gets audio duration using AudioContext
 */
async function getAudioDuration(file: File): Promise<number> {
    const audioContext = new AudioContext();
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const durationMs = audioBuffer.duration * 1000;
    await audioContext.close();
    return durationMs;
}

/**
 * Gets video duration using HTML video element
 */
async function getVideoDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
            URL.revokeObjectURL(video.src);
            resolve(video.duration * 1000);
        };
        video.onerror = () => {
            URL.revokeObjectURL(video.src);
            reject(new Error('Failed to load video metadata'));
        };
        video.src = URL.createObjectURL(file);
    });
}

/**
 * Creates a mixed AudioBuffer from multiple audio sources with offsets
 */
async function createMixedAudioBuffer(
    audioTracks: AudioTrack[],
    videoFile: File | null,
    includeVideoAudio: boolean,
    targetDurationMs: number,
): Promise<AudioBuffer> {
    const sampleRate = 44100;
    const numberOfChannels = 2;
    const totalSamples = Math.ceil((targetDurationMs / 1000) * sampleRate);

    const audioContext = new AudioContext({ sampleRate });
    const mixedBuffer = audioContext.createBuffer(numberOfChannels, totalSamples, sampleRate);

    // Decode all audio tracks
    for (const track of audioTracks) {
        const arrayBuffer = await track.file.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const offsetSamples = Math.floor((track.offsetMs / 1000) * sampleRate);

        for (let channel = 0; channel < numberOfChannels; channel++) {
            const outputData = mixedBuffer.getChannelData(channel);
            const sourceChannel = channel < audioBuffer.numberOfChannels ? channel : 0;
            const sourceData = audioBuffer.getChannelData(sourceChannel);

            for (let i = 0; i < sourceData.length; i++) {
                const targetIndex = offsetSamples + i;
                if (targetIndex >= 0 && targetIndex < totalSamples) {
                    outputData[targetIndex] += sourceData[i];
                }
            }
        }
    }

    // Include video audio if requested
    if (includeVideoAudio && videoFile) {
        const videoArrayBuffer = await videoFile.arrayBuffer();
        const videoAudioBuffer = await audioContext.decodeAudioData(videoArrayBuffer.slice(0));

        for (let channel = 0; channel < numberOfChannels; channel++) {
            const outputData = mixedBuffer.getChannelData(channel);
            const sourceChannel = channel < videoAudioBuffer.numberOfChannels ? channel : 0;
            const sourceData = videoAudioBuffer.getChannelData(sourceChannel);

            for (let i = 0; i < sourceData.length && i < totalSamples; i++) {
                outputData[i] += sourceData[i];
            }
        }
    }

    // Normalize to prevent clipping
    for (let channel = 0; channel < numberOfChannels; channel++) {
        const outputData = mixedBuffer.getChannelData(channel);
        let maxVal = 0;
        for (let i = 0; i < outputData.length; i++) {
            const absVal = Math.abs(outputData[i]);
            if (absVal > maxVal) maxVal = absVal;
        }
        if (maxVal > 1) {
            for (let i = 0; i < outputData.length; i++) {
                outputData[i] /= maxVal;
            }
        }
    }

    await audioContext.close();
    return mixedBuffer;
}

/**
 * Mixes video with audio tracks using mediabunny
 * Uses the audio process callback to replace the original audio with our mixed audio
 */
async function mixVideoWithAudio(
    videoFile: File,
    audioTracks: AudioTrack[],
    includeVideoAudio: boolean,
    videoDurationMs: number,
): Promise<Blob> {
    const {
        Input,
        Output,
        Conversion,
        BlobSource,
        BufferTarget,
        Mp4OutputFormat,
        ALL_FORMATS,
        AudioSample,
    } = await import('mediabunny');

    const mixedAudioBuffer = await createMixedAudioBuffer(
        audioTracks,
        videoFile,
        includeVideoAudio,
        videoDurationMs,
    );

    // Check if video has audio track
    const videoInput = new Input({
        source: new BlobSource(videoFile),
        formats: ALL_FORMATS,
    });
    const hasAudioTrack = (await videoInput.getPrimaryAudioTrack()) !== null;
    videoInput.dispose();

    let resultBlob: Blob;

    if (hasAudioTrack) {
        // If video has audio, use Conversion with audio processing to replace it
        const input = new Input({
            source: new BlobSource(videoFile),
            formats: ALL_FORMATS,
        });

        const output = new Output({
            format: new Mp4OutputFormat(),
            target: new BufferTarget(),
        });

        // Track position in our mixed buffer
        const sampleRate = mixedAudioBuffer.sampleRate;
        const numberOfChannels = mixedAudioBuffer.numberOfChannels;
        let sampleIndex = 0;

        const conversion = await Conversion.init({
            input,
            output,
            video: {}, // Keep video as-is
            audio: {
                codec: 'aac',
                bitrate: 128000,
                sampleRate,
                numberOfChannels,
                forceTranscode: true,
                process: (sample) => {
                    // Replace original audio samples with our mixed audio
                    const numFrames = sample.numberOfFrames;
                    const numChannels = sample.numberOfChannels;

                    // Create interleaved f32 data (required format: f32)
                    const dataSize = numFrames * numChannels * 4; // 4 bytes per f32
                    const buffer = new ArrayBuffer(dataSize);
                    const dataView = new Float32Array(buffer);

                    for (let i = 0; i < numFrames; i++) {
                        for (let ch = 0; ch < numChannels; ch++) {
                            const mixedChannel = ch < mixedAudioBuffer.numberOfChannels ? ch : 0;
                            const channelData = mixedAudioBuffer.getChannelData(mixedChannel);
                            const srcIndex = sampleIndex + i;
                            const destIndex = i * numChannels + ch;

                            if (srcIndex < channelData.length) {
                                dataView[destIndex] = channelData[srcIndex];
                            } else {
                                dataView[destIndex] = 0;
                            }
                        }
                    }

                    sampleIndex += numFrames;

                    // Create a new AudioSample with the replaced data
                    return new AudioSample({
                        format: 'f32',
                        sampleRate: sample.sampleRate,
                        numberOfChannels: numChannels,
                        timestamp: sample.timestamp,
                        data: buffer,
                    });
                },
            },
        });

        await conversion.execute();

        const videoResult = output.target.buffer;
        input.dispose();

        if (!videoResult) {
            throw new Error('Failed to create video output');
        }

        resultBlob = new Blob([videoResult], { type: 'video/mp4' });
    } else {
        // If video has no audio, convert the WAV to get an audio-only MP4,
        // then we need a different strategy. For now, just output video without audio.

        const videoInputNew = new Input({
            source: new BlobSource(videoFile),
            formats: ALL_FORMATS,
        });

        const videoOutput = new Output({
            format: new Mp4OutputFormat(),
            target: new BufferTarget(),
        });

        const videoConversion = await Conversion.init({
            input: videoInputNew,
            output: videoOutput,
            video: {},
            audio: { discard: true },
        });

        await videoConversion.execute();
        const videoOnlyBuffer = videoOutput.target.buffer;
        videoInputNew.dispose();

        if (!videoOnlyBuffer) {
            throw new Error('Failed to create video-only output');
        }

        // For videos without audio, just return video-only for now
        // Users can add audio by ensuring the original video has an audio track
        resultBlob = new Blob([videoOnlyBuffer], { type: 'video/mp4' });
    }

    return resultBlob;
}

export function useMediaBunnyMixer(): UseMediaBunnyMixerReturn {
    const [state, setState] = useState<MixerState>({
        videoFile: null,
        videoDurationMs: 0,
        videoPreviewUrl: null,
        audioTracks: [],
        includeVideoAudio: false,
        isProcessing: false,
        previewUrl: null,
    });

    const previewUrlRef = useRef<string | null>(null);
    const videoPreviewUrlRef = useRef<string | null>(null);

    // Cleanup URLs on unmount
    useEffect(() => {
        return () => {
            if (previewUrlRef.current) {
                URL.revokeObjectURL(previewUrlRef.current);
            }
            if (videoPreviewUrlRef.current) {
                URL.revokeObjectURL(videoPreviewUrlRef.current);
            }
        };
    }, []);

    const setVideoFile = useCallback(async (file: File | null) => {
        // Cleanup old preview URL
        if (videoPreviewUrlRef.current) {
            URL.revokeObjectURL(videoPreviewUrlRef.current);
            videoPreviewUrlRef.current = null;
        }
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }

        if (!file) {
            setState((prev) => ({
                ...prev,
                videoFile: null,
                videoDurationMs: 0,
                videoPreviewUrl: null,
                previewUrl: null,
            }));
            return;
        }

        const durationMs = await getVideoDuration(file);
        const previewUrl = URL.createObjectURL(file);
        videoPreviewUrlRef.current = previewUrl;

        setState((prev) => ({
            ...prev,
            videoFile: file,
            videoDurationMs: durationMs,
            videoPreviewUrl: previewUrl,
            previewUrl: null,
        }));
    }, []);

    const addAudioTrack = useCallback(async (file: File) => {
        const durationMs = await getAudioDuration(file);
        const track: AudioTrack = {
            id: generateId(),
            file,
            name: file.name,
            offsetMs: 0,
            durationMs,
        };

        setState((prev) => ({
            ...prev,
            audioTracks: [...prev.audioTracks, track],
        }));
    }, []);

    const removeAudioTrack = useCallback((id: string) => {
        setState((prev) => ({
            ...prev,
            audioTracks: prev.audioTracks.filter((track) => track.id !== id),
        }));
    }, []);

    const updateAudioTrackOffset = useCallback((id: string, offsetMs: number) => {
        setState((prev) => ({
            ...prev,
            audioTracks: prev.audioTracks.map((track) =>
                track.id === id ? { ...track, offsetMs } : track,
            ),
        }));
    }, []);

    const setIncludeVideoAudio = useCallback((include: boolean) => {
        setState((prev) => ({
            ...prev,
            includeVideoAudio: include,
        }));
    }, []);

    const generatePreview = useCallback(async () => {
        if (!state.videoFile || (state.audioTracks.length === 0 && !state.includeVideoAudio)) {
            return;
        }

        setState((prev) => ({ ...prev, isProcessing: true }));

        const blob = await mixVideoWithAudio(
            state.videoFile,
            state.audioTracks,
            state.includeVideoAudio,
            state.videoDurationMs,
        );

        // Cleanup old preview URL
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
        }

        const previewUrl = URL.createObjectURL(blob);
        previewUrlRef.current = previewUrl;

        setState((prev) => ({
            ...prev,
            isProcessing: false,
            previewUrl,
        }));
    }, [state.videoFile, state.audioTracks, state.includeVideoAudio, state.videoDurationMs]);

    const downloadVideo = useCallback(async () => {
        if (!state.videoFile) {
            return;
        }

        setState((prev) => ({ ...prev, isProcessing: true }));

        const blob = await mixVideoWithAudio(
            state.videoFile,
            state.audioTracks,
            state.includeVideoAudio,
            state.videoDurationMs,
        );

        // Trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mixed_${state.videoFile.name}`;
        document.body.appendChild(a);
        a.click();
        window.setTimeout(() => {
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
        }, 1000);

        setState((prev) => ({
            ...prev,
            isProcessing: false,
        }));
    }, [state.videoFile, state.audioTracks, state.includeVideoAudio, state.videoDurationMs]);

    const reset = useCallback(() => {
        // Cleanup URLs
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        if (videoPreviewUrlRef.current) {
            URL.revokeObjectURL(videoPreviewUrlRef.current);
            videoPreviewUrlRef.current = null;
        }

        setState({
            videoFile: null,
            videoDurationMs: 0,
            videoPreviewUrl: null,
            audioTracks: [],
            includeVideoAudio: false,
            isProcessing: false,
            previewUrl: null,
        });
    }, []);

    return {
        state,
        setVideoFile,
        addAudioTrack,
        removeAudioTrack,
        updateAudioTrackOffset,
        setIncludeVideoAudio,
        generatePreview,
        downloadVideo,
        reset,
    };
}

export default useMediaBunnyMixer;
